import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../../store/store';
import {
  getGetLoanApplicationQueryKey,
  useGetLoanApplication,
  useResolveApprovalCondition,
  useUploadDocument
} from '../../services/loanService';
import { ApprovalCondition } from '../../services/schemas/loanServiceSchemas/approvalCondition';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/rolePermissions';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreview';

/**
 * The Credit Committee's decision and the conditions it attached, kept with the application for
 * as long as it lives.
 *
 * Conditions used to live in someone's memory. Here each one stays Outstanding until it is
 * fulfilled -- with a note or a document showing how -- or waived by someone entitled to set it
 * aside. A "before disbursement" condition that is still outstanding blocks the payout.
 */

interface ApprovalConditionsCardProps {
  applicationId?: string;
  /** Called after a condition changes, for a page holding its own copy of the application. */
  onChanged?: () => void;
}

type Resolution = { condition: ApprovalCondition; action: 'FULFILLED' | 'WAIVED' };

const STATUS_STYLES: Record<string, { label: string; className: string; icon: string }> = {
  OUTSTANDING: { label: 'Outstanding', className: 'bg-warning-subtle text-warning-emphasis', icon: 'fa-clock-o' },
  FULFILLED: { label: 'Fulfilled', className: 'bg-success-subtle text-success-emphasis', icon: 'fa-check' },
  WAIVED: { label: 'Waived', className: 'bg-secondary-subtle text-secondary-emphasis', icon: 'fa-ban' }
};

const today = () => new Date().toISOString().slice(0, 10);

const ApprovalConditionsCard: React.FC<ApprovalConditionsCardProps> = ({ applicationId, onChanged }) => {
  const queryClient = useQueryClient();
  const user = useSelector((state: RootState) => state.auth.user);
  const recordedBy = user?.name || user?.username;
  const { hasPermission } = usePermissions();
  const canWaive = hasPermission(Permission.WAIVE_CONDITIONS);
  const documentPreview = useDocumentPreview(message => toast.error(message));

  const [resolving, setResolving] = React.useState<Resolution | null>(null);
  const [note, setNote] = React.useState('');
  const [file, setFile] = React.useState<File | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const { data: application, isLoading } = useGetLoanApplication(
    applicationId || '',
    { query: { enabled: !!applicationId } }
  );
  const uploadDocument = useUploadDocument();
  const resolveCondition = useResolveApprovalCondition();

  if (!applicationId || isLoading || !application) return null;

  const conditions: ApprovalCondition[] = application.approvalConditions || [];
  if (!application.committeeDecision && conditions.length === 0) return null;

  // Outstanding first, then the rest in the order they were set.
  const ordered = [...conditions].sort((a, b) =>
    Number(b.status === 'OUTSTANDING') - Number(a.status === 'OUTSTANDING')
    || String(a.createdDate || '').localeCompare(String(b.createdDate || '')));
  const outstanding = conditions.filter(c => c.status === 'OUTSTANDING');
  const blocking = outstanding.filter(c => c.conditionType === 'BEFORE_DISBURSEMENT');
  const approved = application.committeeDecision === 'APPROVE';

  const openResolution = (condition: ApprovalCondition, action: Resolution['action']) => {
    setResolving({ condition, action });
    setNote('');
    setFile(null);
  };

  const canSave = resolving
    ? (resolving.action === 'WAIVED' ? !!note.trim() : (!!note.trim() || !!file))
    : false;

  const handleSave = async () => {
    if (!resolving?.condition.id || !canSave) return;
    setIsSaving(true);
    try {
      let documentKey: string | undefined;
      if (file && resolving.action === 'FULFILLED') {
        const uploaded = await uploadDocument.mutateAsync({
          loanApplicationId: applicationId,
          data: {
            file,
            documentType: 'APPROVAL_CONDITION_EVIDENCE',
            description: `Evidence for approval condition: ${resolving.condition.description || ''}`.slice(0, 250)
          }
        });
        documentKey = uploaded.documentKey || uploaded.id;
      }
      await resolveCondition.mutateAsync({
        applicationId,
        conditionId: resolving.condition.id,
        data: {
          status: resolving.action,
          note: note.trim() || undefined,
          documentKey,
          recordedBy
        }
      });
      toast.success(resolving.action === 'WAIVED' ? 'Condition waived' : 'Condition marked fulfilled');
      setResolving(null);
      await queryClient.invalidateQueries({ queryKey: getGetLoanApplicationQueryKey(applicationId) });
      onChanged?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Could not update the condition');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
        <h6 className="card-title mb-0">
          <i className="fa fa-gavel me-2"></i>
          Credit Committee decision
        </h6>
        <div className="d-flex align-items-center gap-2">
          {outstanding.length > 0 && (
            <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis">
              {outstanding.length} outstanding
            </span>
          )}
          {application.committeeDecision && (
            <span className={`badge rounded-pill ${approved
              ? 'bg-success-subtle text-success-emphasis'
              : 'bg-danger-subtle text-danger-emphasis'}`}>
              <i className={`fa ${approved ? 'fa-check-circle' : 'fa-times-circle'} me-1`}></i>
              {approved ? 'Approved' : 'Rejected'}
            </span>
          )}
        </div>
      </div>

      <div className="card-body">
        {application.committeeDecision && (
          <>
            {approved && application.approvedAmount != null && (
              <div className="mb-2">
                <span className="small text-muted me-2">Approved amount</span>
                <span className="fw-semibold">{formatCurrency(application.approvedAmount)}</span>
              </div>
            )}
            {application.approvalRationale && (
              <>
                <div className="small text-muted mb-1">Rationale</div>
                <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{application.approvalRationale}</p>
              </>
            )}
            {application.committeeDecidedAt && (
              <div className="small text-muted">
                <i className="fa fa-user-o me-1"></i>
                {application.committeeDecidedByName ? `Decided by ${application.committeeDecidedByName}` : 'Decided'}
                {` · ${formatDateTime(application.committeeDecidedAt)}`}
              </div>
            )}
          </>
        )}

        {blocking.length > 0 && (
          <div className="alert alert-warning mt-3 mb-0 py-2 small">
            <i className="fa fa-lock me-2"></i>
            {blocking.length === 1
              ? '1 condition must be fulfilled or waived before the loan can be disbursed.'
              : `${blocking.length} conditions must be fulfilled or waived before the loan can be disbursed.`}
          </div>
        )}
      </div>

      {ordered.length > 0 && (
        <div className="list-group list-group-flush">
          {ordered.map(condition => {
            const style = STATUS_STYLES[condition.status || 'OUTSTANDING'] || STATUS_STYLES.OUTSTANDING;
            const overdue = condition.status === 'OUTSTANDING' && !!condition.dueDate && condition.dueDate < today();
            return (
              <div key={condition.id} className="list-group-item">
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-semibold small" style={{ whiteSpace: 'pre-wrap' }}>{condition.description}</div>
                    <div className="d-flex flex-wrap gap-2 mt-1 small">
                      <span className={`badge rounded-pill ${style.className}`}>
                        <i className={`fa ${style.icon} me-1`}></i>{style.label}
                      </span>
                      <span className="badge rounded-pill bg-light text-dark border">
                        {condition.conditionType === 'BEFORE_DISBURSEMENT' ? 'Before disbursement' : 'After disbursement'}
                      </span>
                      {condition.dueDate && (
                        <span className={overdue ? 'text-danger fw-semibold' : 'text-muted'}>
                          Due {formatDate(condition.dueDate)}{overdue && ' · overdue'}
                        </span>
                      )}
                    </div>
                    {condition.status !== 'OUTSTANDING' && (
                      <div className="small text-muted mt-1">
                        {condition.resolutionNote && <div style={{ whiteSpace: 'pre-wrap' }}>{condition.resolutionNote}</div>}
                        <div>
                          {condition.status === 'WAIVED' ? 'Waived' : 'Fulfilled'}
                          {condition.resolvedByName && ` by ${condition.resolvedByName}`}
                          {condition.resolvedAt && ` · ${formatDateTime(condition.resolvedAt)}`}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2 align-items-start">
                    {condition.documentKey && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => documentPreview.open(condition.documentKey!, 'Condition evidence')}
                        disabled={documentPreview.openingKey === condition.documentKey}
                      >
                        <i className="fa fa-eye me-1"></i>Evidence
                      </Button>
                    )}
                    {condition.status === 'OUTSTANDING' && (
                      <>
                        <Button variant="outline-success" size="sm" onClick={() => openResolution(condition, 'FULFILLED')}>
                          <i className="fa fa-check me-1"></i>Mark fulfilled
                        </Button>
                        {canWaive && (
                          <Button variant="outline-secondary" size="sm" onClick={() => openResolution(condition, 'WAIVED')}>
                            Waive
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={!!resolving} onHide={() => !isSaving && setResolving(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {resolving?.action === 'WAIVED' ? 'Waive condition' : 'Mark condition fulfilled'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small mb-3" style={{ whiteSpace: 'pre-wrap' }}>{resolving?.condition.description}</p>
          <label htmlFor="condition-note" className="form-label small">
            {resolving?.action === 'WAIVED'
              ? 'Why is this condition being waived?'
              : 'How was it met? Add a note, a document, or both.'}
          </label>
          <textarea
            id="condition-note"
            className="form-control mb-3"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={isSaving}
          />
          {resolving?.action === 'FULFILLED' && (
            <>
              <label htmlFor="condition-file" className="form-label small">Supporting document (optional)</label>
              <input
                id="condition-file"
                type="file"
                className="form-control"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setFile(e.target.files?.[0] || null)}
                disabled={isSaving}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setResolving(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant={resolving?.action === 'WAIVED' ? 'secondary' : 'success'}
            size="sm"
            onClick={handleSave}
            disabled={!canSave || isSaving}
          >
            {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
            {resolving?.action === 'WAIVED' ? 'Waive condition' : 'Mark fulfilled'}
          </Button>
        </Modal.Footer>
      </Modal>

      <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
    </div>
  );
};

export default ApprovalConditionsCard;
